<script>
/**
 * @typedef { import('vue').Ref } Ref
 */

/**
 * Represents the links and attachments of an applicant.
 *
 * @typedef {Object} ApplicationSource
 * @property {?string} referrerEmail - Referrer email of the applicant.
 * @property {?string} recruitmentLink - Link to the related staffing request.
 * @property {?('applied'|'sourced'|'referred')} source - Source of the applicant.
 * @property {?number} sourcerId - Sourcer ID of the applicant.
 * @property {?number} vettingPlanId - Vetting plan ID of the application.
 */

/**
 * Represents the profile of a user, including personal details and roles within the organization.
 *
 * @typedef {Object} User
 * @property {string} name - Full name of the staff member.
 * @property {string} email - Email address of the staff member.
 * @property {?string} avatar_url - URL of the avatar image. Null if no avatar is set.
 * @property {number} id - Unique identifier for the staff member.
 * @property {boolean} active - Indicates whether the staff member is currently active.
 * @property {string} alias - An alias used for the staff member.
 * @property {Role[]} roles - Array of roles assigned to the staff member.
 *
 * @typedef {Object} Role
 * @property {number} id - Unique identifier for the role.
 * @property {string} name - Technical name of the role.
 * @property {string} display_name - Human-readable name of the role.
 */
export default {
  name: 'ApplicationFormSource',
};
</script>
<script setup>
import {
  BaseInputDropdown,
  BaseInputField,
  BaseRadioGroup,
  BaseTooltip,
} from '@/components/Base';
import BaseIcon from '@/components/Base/BaseIcon.vue';
import PSeparator from '@/components/ProxifyUI/PSeparator.vue';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { stringUtils } from '@/utils';

const props = defineProps({
  referrerEmail: {
    type: String,
    required: false,
    default() {
      return '';
    },
  },
  referrerId: {
    type: Number,
    required: false,
    default: undefined,
  },
  recruitmentLink: {
    type: String,
    required: false,
    default() {
      return '';
    },
  },
  source: {
    type: String,
    required: false,
    default() {
      return '';
    },
  },
  sourcerId: {
    type: Number,
    required: false,
    default: undefined,
  },
  isEditing: {
    type: Boolean,
    required: false,
    default: false,
  },
  vettingPlanId: {
    type: Number,
    required: false,
    default: undefined,
  },
  vettingPlanVisible: {
    type: Boolean,
    required: false,
    default: true,
  },
  vettingPlanDisabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  requiredFields: {
    type: Array,
    required: false,
    default() {
      return [];
    },
  },
});

const emit = defineEmits([
  'update:referrer-email',
  'update:recruitment-link',
  'update:source',
  'update:sourcer-id',
  'update:vetting-plan-id',
]);

const store = useStore();
const sourcers = computed(() =>
  store.state.applicant.sourcers.filter(
    (sourcer) => sourcer.active || sourcer.id === props.sourcerId
  )
);
const sources = computed(() => store.state.application.sources);
// An applicant already linked to a referrer keeps that referrer, so the origin
// cannot be changed and the referrer is shown in place of the referral e-mail.
const isReferrerLinked = computed(
  () => props.isEditing && Boolean(props.referrerId)
);
// Only sources allowed on creation are offered for a new application,
// while an existing one can be switched to any source.
const availableSources = computed(() => {
  const options = props.isEditing
    ? sources.value
    : sources.value.filter(({ availableOnCreating }) => availableOnCreating);
  return isReferrerLinked.value
    ? options.map((option) => ({ ...option, disabled: true }))
    : options;
});
/** @type {Ref<ApplicationSource>} */
const form = ref({});
// Sourcer and referral e-mail are rendered beside the source options,
// which only fit on a single row while the list of options is short.
const isSourceDetailVisible = computed(() =>
  ['sourced', 'referred'].includes(form.value.source)
);
const isSourceListVertical = computed(
  () => availableSources.value.length > 2 && isSourceDetailVisible.value
);
const isSourcerRequired = computed(
  () =>
    form.value.source === 'sourced' && props.requiredFields.includes('Sourcer')
);
const isReferralEmailRequired = computed(
  () =>
    form.value.source === 'referred' &&
    props.requiredFields.includes('Referral e-mail') &&
    !isReferrerLinked.value
);
const sourceDetailLabel = computed(() => {
  if (form.value.source === 'sourced') return 'Sourcer';
  return isReferrerLinked.value ? 'Referrer ID' : 'Referral e-mail';
});
/** @type {Ref<User>} */
const currentUser = computed(() => store.state.auth.user);
const initialSource = computed(() => {
  const isTalentSourcer = (currentUser.value.roles ?? [])
    .map((role) => role.name)
    .includes('talent-sourcer');
  return isTalentSourcer ? currentUser.value.id : null;
});

const vettingPlans = computed(() =>
  store.state.application.vettingPlans
    .filter(({ active, id }) => active || id === props.vettingPlanId)
    .map((plan) => ({ ...plan, name: `(${plan.name})` }))
);

watch(
  () => ({
    source: props.source,
    sourcerId: props.sourcerId,
    referrerEmail: props.referrerEmail,
    recruitmentLink: props.recruitmentLink,
    vettingPlanId: props.vettingPlanId,
  }),
  /** @param {ApplicationSource} newProps */
  (newProps) => {
    form.value = {
      source: newProps.source || (initialSource.value ? 'sourced' : undefined),
      sourcerId:
        !newProps.source || newProps.source === 'sourced'
          ? (newProps.sourcerId ?? initialSource.value)
          : null,
      referrerEmail:
        newProps.source === 'referred' ? newProps.referrerEmail : null,
      recruitmentLink: stringUtils.modifyUrlProtocol(
        newProps.recruitmentLink,
        'remove'
      ),
      vettingPlanId: newProps.vettingPlanId ?? null,
    };
  },
  { immediate: true, deep: true }
);

watch(
  () => form.value,
  /** @param {ApplicationSource} newForm */
  (newForm) => {
    emit(
      'update:referrer-email',
      newForm.source === 'referred' ? newForm.referrerEmail : null
    );
    emit(
      'update:recruitment-link',
      stringUtils.modifyUrlProtocol(newForm.recruitmentLink, 'add') || null
    );
    emit('update:source', newForm.source);
    emit(
      'update:sourcer-id',
      newForm.source === 'sourced' ? newForm.sourcerId : null
    );
    emit('update:vetting-plan-id', newForm.vettingPlanId);
  },
  { immediate: true, deep: true }
);
</script>
<template>
  <div class="flex flex-col gap-4">
    <div class="flex gap-4">
      <div>
        <div class="text-body-sm font-medium text-proxify-gray-700 mb-2">
          Applicant origin
        </div>
        <BaseRadioGroup
          v-model="form.source"
          name="Source"
          :options="availableSources"
          option-value="value"
          option-label="name"
          class="w-fit"
          :orientation="isSourceListVertical ? 'vertical' : 'horizontal'"
          data-testid="source-radio-group"
        />
      </div>
      <PSeparator
        v-show="!isSourceListVertical && isSourceDetailVisible"
        vertical
        class="h-6 mt-auto mb-2.5"
      />
      <div class="w-full">
        <div
          v-show="isSourceDetailVisible"
          class="text-body-sm font-medium text-proxify-gray-700 mb-2"
          data-testid="source-detail-label"
        >
          {{ sourceDetailLabel }}
          <span v-if="isSourcerRequired || isReferralEmailRequired"> * </span>
        </div>
        <BaseInputDropdown
          v-show="form.source === 'sourced'"
          v-model="form.sourcerId"
          name="Sourcer"
          :options="sourcers"
          emit-value
        />
        <BaseInputField
          v-show="form.source === 'referred' && !isReferrerLinked"
          v-model="form.referrerEmail"
          name="Referral e-mail"
        />
        <BaseInputField
          v-if="isReferrerLinked && form.source === 'referred'"
          :model-value="referrerId"
          name="Referrer ID"
          disable
        />
      </div>
    </div>
    <div
      v-show="vettingPlanVisible"
      data-name="Vetting plan"
    >
      <div
        class="text-body-sm font-medium mb-2 flex items-center gap-1"
        :class="
          vettingPlanDisabled
            ? 'text-proxify-gray-400'
            : 'text-proxify-gray-700'
        "
      >
        <span>
          Vetting plan
          <span
            v-if="
              requiredFields.includes('Vetting plan') && !vettingPlanDisabled
            "
          >
            *
          </span>
        </span>
        <div class="flex items-center cursor-pointer">
          <BaseIcon
            name="help-circle"
            class="text-proxify-gray-400"
            size="14px"
          />
          <BaseTooltip
            max-width="260px"
            :offset="[0, 6]"
          >
            This option defines the sequence of stages used to evaluate the
            applicant when the application is created.
          </BaseTooltip>
        </div>
      </div>
      <BaseInputDropdown
        v-model="form.vettingPlanId"
        name="Vetting plan"
        placeholder="Select vetting plan"
        option-label="label"
        option-sublabel="name"
        :options="vettingPlans"
        :disable="vettingPlanDisabled"
        emit-value
      />
    </div>
    <div>
      <div class="text-body-sm font-medium text-proxify-gray-700 mb-2">
        Related staffing request
      </div>
      <BaseInputField
        v-model="form.recruitmentLink"
        leading-text="https://"
        type="url"
        hide-bottom-space
        :hint="undefined"
        name="Related staffing request"
        data-testid="recruitment-link-input"
      />
    </div>
  </div>
</template>
